import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Button from './ui/button';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Prevent background scroll and auto-focus email input
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    // Auto-focus the email input
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!email) {
      setError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setError('Invalid email address');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/auth/magic-link`, { email });
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-background-base/60 backdrop-blur-md pointer-events-auto pb-16" style={{ pointerEvents: 'auto' }} />
      <div className="w-full max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl flex flex-col items-center relative min-h-[540px] z-50" style={{ pointerEvents: 'auto' }}>
        <div className="flex flex-col items-center w-full">
          <div className="flex items-center justify-center mb-6 mt-2">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M32 8C41.9411 8 50 16.0589 50 26C50 35.9411 41.9411 44 32 44C22.0589 44 14 35.9411 14 26C14 16.0589 22.0589 8 32 8Z" fill="#f9fafb"/>
              <path d="M32 56C41.9411 56 50 47.9411 50 38C50 28.0589 41.9411 20 32 20C22.0589 20 14 28.0589 14 38C14 47.9411 22.0589 56 32 56Z" fill="#f9fafb" fillOpacity="0.7"/>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-text-primary text-center mb-1">Welcome back to ThinkMate</h2>
          <p className="text-base text-text-secondary text-center mb-6">Ready to build momentum today?</p>
          {success ? (
            <div className="w-full bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-4 text-center">
              Magic link sent! Check your inbox.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="w-full">
              <div className="mb-4">
                <input
                  ref={emailInputRef}
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={`shadow appearance-none border rounded-lg w-full py-3 px-4 text-text-primary leading-tight focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_4px_rgba(147,197,253,0.5)] transition-all duration-300 bg-gray-50 ${error ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="your@momentum.com"
                />
                {error && <p className="text-red-500 text-xs italic mt-2">{error}</p>}
              </div>
              <Button
                type="submit"
                ambient={true}
                size="lg"
                className="w-full py-3 mt-2 text-lg"
              >
                Send Magic Link
              </Button>
              <div className="text-center mt-4">
                <a className="text-text-secondary hover:text-text-primary font-semibold underline transition" href="/signup">
                  Need an account? Sign up
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;